import { useEffect, useState } from "react";
import { Loader2, TrendingUp, Target, CheckCircle2, FileText, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const money0 = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export const HospitalRebatePanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("hospital_token");
        const r = await fetch(`${API}/api/rebates/my-position`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.detail || "Could not load");
        setData(await r.json());
      } catch (e) {
        setErr(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-3 p-10 text-slate-500" data-testid="rebate-panel-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your rebate position…
      </div>
    );
  }
  if (err) {
    return (
      <div className="rounded-xl border border-amber-300 bg-amber-50 p-6" data-testid="rebate-panel-error">
        <p className="text-sm text-amber-900">{err}</p>
      </div>
    );
  }

  const q = data.current_quarter;
  const a = data.annual;
  const enrolled = !!data.enrollment;
  const pct = q.next_tier_at ? Math.min(100, (q.qualified_spend / q.next_tier_at) * 100) : 100;

  return (
    <div className="space-y-6" data-testid="hospital-rebate-panel">
      {!enrolled && (
        <div className="rounded-xl border border-sky-200 bg-sky-50 p-6" data-testid="rebate-not-enrolled">
          <h3 className="text-base font-bold text-slate-900 mb-1.5">
            You are not enrolled in the rebate program yet
          </h3>
          <p className="text-sm text-slate-600 mb-4 leading-relaxed">
            The figures below show what your current backorder volume would have earned. Enrollment
            is a one-page agreement and there is no GPO contract required.
          </p>
          <a href="/solutions/rebate-program#enroll" data-testid="rebate-enroll-link"
            className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-sky-500 transition-colors">
            Review the program <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      )}

      {q.pre_launch && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-6" data-testid="rebate-pre-launch">
          <h3 className="text-base font-bold text-amber-900 mb-1.5">
            The rebate program begins January 1, 2027
          </h3>
          <p className="text-sm text-amber-900/80 leading-relaxed">
            The figures below are your current backorder volume, shown as a baseline so you can see
            where you would land at launch. No rebate accrues before {q.launch_period}. Your first
            rebate quarter closes March 31, 2027 and the first statement arrives February 3, 2027.
          </p>
        </div>
      )}

      {/* position */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="bg-slate-900 p-6 md:p-8">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-sky-300 mb-1.5">
            {q.pre_launch
              ? `Baseline volume · ${q.period} · pre-launch`
              : `Accrued this quarter · ${q.period}`}
          </p>
          <p className="text-4xl md:text-5xl font-black text-white tabular-nums tracking-tight"
            data-testid="rebate-accrued">
            {money(q.pre_launch ? q.qualified_spend : q.accrued_total)}
          </p>
          <p className="text-sm text-slate-300 mt-2.5">
            {q.pre_launch
              ? `Would qualify at ${q.tier_label} · ${q.base_rate_pct}% on `
              : `${q.tier_label} · ${q.base_rate_pct}% base · blended ${q.blended_rate_pct}% on `}
            <span className="tabular-nums">{money(q.qualified_spend)}</span> qualified spend
          </p>
          <p className="text-[11px] text-slate-500 mt-3">
            {q.pre_launch
              ? `Period ${q.quarter_start} to ${q.quarter_end} · rebate accrual starts ${q.launch_date}`
              : `Period ${q.quarter_start} to ${q.quarter_end} · payment due by ${q.payout_due_by}`}
          </p>
        </div>

        {/* tier progress */}
        {q.next_tier && (
          <div className="p-6 md:p-8 border-b border-slate-200" data-testid="rebate-next-tier">
            <div className="flex items-end justify-between gap-4 mb-2.5">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1">
                  Gap to {q.next_tier} ({q.next_tier_rate_pct}%)
                </p>
                <p className="text-2xl font-bold text-slate-900 tabular-nums">
                  {money(q.gap_to_next_tier)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1">
                  Worth if you reach it
                </p>
                <p className="text-2xl font-bold text-sky-600 tabular-nums">
                  +{money(q.upside_if_reached)}
                </p>
              </div>
            </div>
            <div className="h-3 rounded-sm bg-slate-100 border border-slate-200 overflow-hidden">
              <div className="h-full bg-sky-600 transition-all duration-700" style={{ width: `${pct}%` }} />
            </div>
            <p className="text-xs text-slate-500 mt-2.5 leading-relaxed">
              The tier you reach applies to the <strong className="text-slate-700">first dollar</strong> of
              the quarter, not only the dollars above the threshold — which is why closing the gap is
              worth more than the gap itself.
            </p>
          </div>
        )}

        {/* components */}
        <div className="grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-200">
          <div className="p-5">
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">Base volume</p>
            <p className="text-lg font-bold text-slate-900 tabular-nums">{money(q.base_amount)}</p>
            <p className="text-[11px] text-slate-500 mt-1">{q.base_rate_pct}% of qualified spend</p>
          </div>
          <div className="p-5">
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">Growth</p>
            <p className={`text-lg font-bold tabular-nums ${q.growth_met ? "text-emerald-600" : "text-slate-400"}`}>
              {money(q.growth_amount)}
            </p>
            <p className="text-[11px] text-slate-500 mt-1">
              {q.growth_pct === null ? "No prior-year quarter to compare" : `${q.growth_pct}% vs last year`}
            </p>
          </div>
          <div className="p-5">
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">Primary source</p>
            <p className={`text-lg font-bold tabular-nums ${q.commitment_met ? "text-emerald-600" : "text-slate-400"}`}>
              {money(q.commitment_amount)}
            </p>
            <p className="text-[11px] text-slate-500 mt-1">
              {q.commitment_met ? "Verified this quarter" : "Awaiting verification"}
            </p>
          </div>
        </div>
      </div>

      {/* annual */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 md:p-7" data-testid="rebate-annual">
        <div className="flex items-start gap-3 mb-4">
          {a.annual_bonus_met
            ? <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
            : <Target className="w-5 h-5 text-sky-600 mt-0.5 shrink-0" />}
          <div>
            <h3 className="text-base font-bold text-slate-900">
              {money0(a.annual_threshold)} annual threshold
            </h3>
            <p className="text-sm text-slate-600 mt-0.5">
              Clearing it adds {a.annual_bonus_rate_pct}% on the full program year.
            </p>
          </div>
        </div>
        <div className="flex items-end justify-between gap-4 mb-2">
          <p className="text-2xl font-bold text-slate-900 tabular-nums">{money(a.ytd_qualified_spend)}</p>
          <p className="text-sm text-slate-500 tabular-nums">
            {a.annual_bonus_met ? "Threshold met" : `${money(a.gap_to_annual_threshold)} to go`}
          </p>
        </div>
        <div className="h-3 rounded-sm bg-slate-100 border border-slate-200 overflow-hidden">
          <div className={`h-full transition-all duration-700 ${a.annual_bonus_met ? "bg-emerald-500" : "bg-sky-600"}`}
            style={{ width: `${Math.min(100, (a.ytd_qualified_spend / a.annual_threshold) * 100)}%` }} />
        </div>
      </div>

      {/* payouts */}
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="rebate-payouts">
        <h3 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4 text-slate-400" /> Rebate payments
        </h3>
        {data.payouts?.length ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200">
                <th className="py-2">Period</th><th className="py-2">Method</th>
                <th className="py-2">Reference</th><th className="py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.payouts.map((p) => (
                <tr key={p.id} className="border-b border-slate-100">
                  <td className="py-2.5 text-slate-700">{p.period}</td>
                  <td className="py-2.5 text-slate-600">{p.method}</td>
                  <td className="py-2.5 text-slate-500 font-mono text-xs">{p.reference || "—"}</td>
                  <td className="py-2.5 text-right font-bold text-slate-900 tabular-nums">{money(p.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-slate-500">
            No payments issued yet. Closed quarters are paid within{" "}
            {data.program.payout_days_after_quarter_close} days.
          </p>
        )}
      </div>

      {/* required notice */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid="rebate-safe-harbor">
        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600 mb-2 flex items-center gap-2">
          <TrendingUp className="w-3.5 h-3.5" /> Required discount notice
        </p>
        <p className="text-xs text-slate-600 leading-relaxed">{data.program.safe_harbor_notice}</p>
      </div>
    </div>
  );
};

export default HospitalRebatePanel;
